import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header';
import databg from '@/assets/products/sc.png';
import { Network, Ship, MapPin, AlertTriangle, Globe, Anchor } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/supply-chain');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/supply-route-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/supply-resilience-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/corporate-dependency-map-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/demand-pulse-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const SupplyChainPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Supply Chain Data API: Company Relationships"
        description="Supply chain data API covering company relationships, supplier networks and trade flows for 25,000+ companies: intelligence and analytics, refreshed daily."
        canonicalUrl="https://axionquant.com/products/supply-chain"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Supply Chain Data API",
          "description": "Supply chain data API covering company relationships, supplier networks, and trade flows. Supply chain intelligence and analytics for 25,000+ companies, refreshed daily.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Supply Chain Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
  docs="/docs/02-alternative-data/supply-chain"
  title="Supply Chain Data API – Map Every Company Relationship"
  body="Supply chain intelligence that traces supplier networks, customer dependencies, and peer relationships across 25,000+ companies. Identify concentration risk, upstream exposure, and competitive positioning with a single supply chain data API call."
  crumb={{name: 'Supply Chain', path:'/products/supply-chain'}}
  tabs={[
    {
      label: 'Supplier Network',
      language: 'python',
      filename: 'supplier_network.py',
      bg: '22.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

ticker    = "AAPL"
suppliers = client.supply_chain.suppliers(ticker)

def fetch_supplier_health(row):
    t       = row["ticker"]
    metrics = client.financials.metrics(t)
    return (t, {
        "ticker":       t,
        "name":         row["name"],
        "gross_margin": metrics["grossMargin"],
        "debt_equity":  metrics["debtToEquity"],
        "roe":          metrics["returnOnEquity"],
    })

df_suppliers = utils.df(suppliers)
health_map   = {}
utils.work(df_suppliers, fetch_supplier_health, health_map)

df_health = utils.df(list(health_map.values()))

visualize.scatter(
    df_health,
    x="debt_equity",
    y="gross_margin",
    hover=["name", "roe"],
)
`,
    },
    {
      label: 'Customer Concentration',
      language: 'python',
      filename: 'customer_concentration.py',
      bg: '23.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

ticker    = "TSM"
customers = client.supply_chain.customers(ticker)

def fetch_customer_size(row):
    t    = row["ticker"]
    snap = client.financials.snapshot(t)
    return (t, {
        "ticker":   t,
        "name":     row["name"],
        "segment":  row.get("segment", "N/A"),
        "revenue":  snap["revenue"],
        "fcf":      snap["freeCashFlow"],
    })

df_customers = utils.df(customers)
size_map     = {}
utils.work(df_customers, fetch_customer_size, size_map)

df_sized = utils.df(list(size_map.values()))

visualize.bar(df_sized, x="name", y="revenue")
`,
    },
    {
      label: 'Upstream ESG Exposure',
      language: 'python',
      filename: 'upstream_esg_exposure.py',
      bg: '24.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

ticker    = "AAPL"
suppliers = client.supply_chain.suppliers(ticker)

def fetch_esg(row):
    t   = row["ticker"]
    esg = client.esg.data(t)
    return (t, {
        "ticker":      t,
        "name":        row["name"],
        "esg_total":   esg["totalEsg"],
        "environment": esg["environmentScore"],
        "governance":  esg["governanceScore"],
        "performance": esg["esgPerformance"],
    })

df_suppliers = utils.df(suppliers)
esg_map      = {}
utils.work(df_suppliers, fetch_esg, esg_map)

df_esg    = utils.df(list(esg_map.values()))
high_risk = utils.filter(df_esg, col="performance", items=["UNDER_PERF"])
ranked    = df_esg.sort_values("esg_total", ascending=False)

visualize.barh(ranked, x="esg_total", y="name")
`,
    },
    {
      label: 'Peer Benchmarking',
      language: 'python',
      filename: 'peer_benchmarking.py',
      bg: '21.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

ticker = "NVDA"
peers  = client.supply_chain.peers(ticker)

focus_metrics = client.financials.metrics(ticker)
focus_snap    = client.financials.snapshot(ticker)

def fetch_peer(row):
    t       = row["ticker"]
    metrics = client.financials.metrics(t)
    snap    = client.financials.snapshot(t)
    return (t, {
        "ticker":       t,
        "gross_margin": metrics["grossMargin"],
        "roe":          metrics["returnOnEquity"],
        "debt_equity":  metrics["debtToEquity"],
        "fcf":          snap["freeCashFlow"],
    })

df_peers  = utils.df([p for p in peers[:6]])
peer_map  = {}
utils.work(df_peers, fetch_peer, peer_map)

all_rows  = list(peer_map.values()) + [{
    "ticker":       ticker,
    "gross_margin": focus_metrics["grossMargin"],
    "roe":          focus_metrics["returnOnEquity"],
    "debt_equity":  focus_metrics["debtToEquity"],
    "fcf":          focus_snap["freeCashFlow"],
}]

df_bench = utils.df(all_rows)

visualize.scatter(
    df_bench,
    x="gross_margin",
    y="roe",
    hover=["ticker", "debt_equity"],
)
`,
    },
  ]}
/>
      <Marquee/>

      <FeaturesGrid
        title="Supply Chain Intelligence. "
        subtitle="Supply chain analytics API and corporate relationships data. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Network size={80} color="var(--purple)" />,
          title: 'Supplier Networks',
          description: 'Map tier-1 through tier-N supplier and customer company relationships. Identify concentration risk, single points of failure, and hidden dependencies across global supply chains.',
        },
        {
          fig: 'FIG 0.2',
          icon: <Ship size={80} color="var(--green)" />,
          title: 'Trade Flows',
          description: 'Track import/export activity, shipping routes, and trade volume trends. Monitor HS code-level flows between countries and economic blocs.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Anchor size={80} color="var(--red)" />,
          title: 'Logistics Data',
          description: 'Port congestion, vessel waiting times, freight rates, and logistics performance metrics. Operationalize global shipping trends in your models.',
        },
        {
          fig: 'FIG 0.4',
          icon: <AlertTriangle size={80} color="var(--purple)" />,
          title: 'Risk Indicators',
          description: 'Geopolitical, climate, and labour disruption alerts. Financial health scores of key suppliers to model contagion risk through the value chain.',
        },
        {
          fig: 'FIG 0.5',
          icon: <MapPin size={80} color="var(--green)" />,
          title: 'Geographic Intelligence',
          description: 'Pinpoint supplier locations, FTZ zones, and logistics hubs. Correlate regional events with potential earnings impact across your portfolio.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global Coverage',
          description: '25,000+ companies, 500+ ports, 1M+ trade routes across 195+ countries. From raw materials to finished goods, we map every node.',
        },
      ]}/>



      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Supply Chain Intelligence Platform"
        headingPlain="Supply chain relationships"
        headingEm="in one API."
        body="Visualize global supplier networks, trade routes, and logistics choke points with institutional-grade supply chain intelligence refreshed daily."
        stats={[
          { v: "25K+",      l: "Companies"       },
          { v: "1M+",       l: "Trade Routes"    },
          { v: "500+",      l: "Ports Monitored" },
          { v: "Daily",     l: "Update Frequency"},
        ]}
        markers={[
  { id: "shanghai", location: [31.2304, 121.4737], label: "Shanghai" },
  { id: "ningbo", location: [29.8683, 121.5440], label: "Ningbo" },
  { id: "shenzhen", location: [22.5431, 114.0579], label: "Shenzhen" },
  { id: "guangzhou", location: [23.1291, 113.2644], label: "Guangzhou" },
  { id: "qingdao", location: [36.0671, 120.3826], label: "Qingdao" },
  { id: "tianjin", location: [39.3434, 117.3616], label: "Tianjin Port" },
  { id: "xiamen", location: [24.4798, 118.0894], label: "Xiamen" },
  { id: "hongkong", location: [22.3193, 114.1694], label: "Hong Kong" },
  { id: "kaohsiung", location: [22.6273, 120.3014], label: "Kaohsiung" },
  { id: "busan", location: [35.1796, 129.0756], label: "Busan" },
  { id: "tokyo", location: [35.6762, 139.6503], label: "Tokyo" },
  { id: "yokohama", location: [35.4437, 139.6380], label: "Yokohama" },
  { id: "nagoya", location: [35.1815, 136.9066], label: "Nagoya" },
  { id: "osaka", location: [34.6937, 135.5023], label: "Osaka" },
  { id: "singapore", location: [1.3521, 103.8198], label: "Singapore" },
  { id: "portklang", location: [3.0019, 101.3928], label: "Port Klang" },
  { id: "tanjungpelepas", location: [1.3546, 103.5492], label: "Tanjung Pelepas" },
  { id: "jakarta", location: [-6.2088, 106.8456], label: "Jakarta" },
  { id: "bangkok", location: [13.7563, 100.5018], label: "Bangkok" },
  { id: "laemchabang", location: [13.0827, 100.8830], label: "Laem Chabang" },
  { id: "hochiminh", location: [10.8231, 106.6297], label: "Ho Chi Minh City" },
  { id: "manila", location: [14.5995, 120.9842], label: "Manila" },
  { id: "colombo", location: [6.9271, 79.8612], label: "Colombo" },
  { id: "mumbai", location: [19.0760, 72.8777], label: "Mumbai" },
  { id: "mundra", location: [22.8392, 69.7213], label: "Mundra Port" },
  { id: "chennai", location: [13.0827, 80.2707], label: "Chennai" },
  { id: "dubai", location: [25.2048, 55.2708], label: "Dubai (Jebel Ali)" },
  { id: "dammam", location: [26.4207, 50.0888], label: "Dammam" },
  { id: "jeddah", location: [21.4858, 39.1925], label: "Jeddah" },
  { id: "salalah", location: [17.0194, 54.0897], label: "Salalah" },
  { id: "suez", location: [29.9668, 32.5498], label: "Suez Canal" },
  { id: "rotterdam", location: [51.9244, 4.4777], label: "Rotterdam" },
  { id: "antwerp", location: [51.2194, 4.4025], label: "Antwerp" },
  { id: "hamburg", location: [53.5511, 9.9937], label: "Hamburg" },
  { id: "bremerhaven", location: [53.5396, 8.5809], label: "Bremerhaven" },
  { id: "felixstowe", location: [51.9636, 1.3511], label: "Felixstowe" },
  { id: "lehavre", location: [49.4944, 0.1079], label: "Le Havre" },
  { id: "barcelona", location: [41.3851, 2.1734], label: "Barcelona" },
  { id: "valencia", location: [39.4699, -0.3763], label: "Valencia" },
  { id: "genoa", location: [44.4056, 8.9463], label: "Genoa" },
  { id: "piraeus", location: [37.9420, 23.6465], label: "Piraeus" },
  { id: "istanbul", location: [41.0082, 28.9784], label: "Istanbul" },
  { id: "casablanca", location: [33.5731, -7.5898], label: "Casablanca" },
  { id: "lagos", location: [6.5244, 3.3792], label: "Lagos" },
  { id: "durban", location: [-29.8587, 31.0218], label: "Durban" },
  { id: "losangeles", location: [33.7405, -118.2626], label: "Los Angeles" },
  { id: "longbeach", location: [33.7701, -118.1937], label: "Long Beach" },
  { id: "oakland", location: [37.8044, -122.2712], label: "Oakland" },
  { id: "seattle", location: [47.6062, -122.3321], label: "Seattle" },
  { id: "vancouver", location: [49.2827, -123.1207], label: "Vancouver" },
  { id: "princerupert", location: [54.3150, -130.3208], label: "Prince Rupert" },
  { id: "houston", location: [29.7604, -95.3698], label: "Houston" },
  { id: "savannah", location: [32.0809, -81.0912], label: "Savannah" },
  { id: "charleston", location: [32.7765, -79.9311], label: "Charleston" },
  { id: "norfolk", location: [36.8508, -76.2859], label: "Norfolk" },
  { id: "newyork", location: [40.7128, -74.0060], label: "New York / Newark" },
  { id: "panama", location: [9.1012, -79.4029], label: "Panama Canal" },
  { id: "manzanillo", location: [19.0501, -104.3188], label: "Manzanillo" },
  { id: "santos", location: [-23.9608, -46.3336], label: "Santos" },
  { id: "buenosaires", location: [-34.6037, -58.3816], label: "Buenos Aires" },
  { id: "sydney", location: [-33.8688, 151.2093], label: "Sydney" },
  { id: "melbourne", location: [-37.8136, 144.9631], label: "Melbourne" },
  { id: "auckland", location: [-36.8485, 174.7633], label: "Auckland" }
]}
      />

        <DataGrid
        title="Supply Chain Data API: See the Connections"
        subtitle="Map the hidden network with supply chain analytics. Identify critical dependencies, single points of failure, and concentration risks across global supply chains before they impact earnings or stock price."
        bg={databg}
        stats={[
          { label: 'Company Relationships', value: '5M+' },
          { label: 'Global Suppliers', value: '20M+' },
          { label: 'Data Sources', value: '1,000+' },
        ]}
      />
      <CardList
        title="Quantify Risk with Supply Chain Intelligence"
        vid={'https://cdn.studio14a.com/axion/products/cards/sc.mp4'}
        points={[
          {
            "title": "Global Supplier & Customer Mapping",
            "details": "Uncover the complete tier-1 through tier-N supplier and customer company relationships network for any public company. Identify revenue concentration and critical dependency risks.",
            "cta": "Map a company's network",
            "url": "/docs"
          },
          {
            "title": "Geographic Risk & Disruption Alerts",
            "details": "Monitor supplier locations for geopolitical, climate, and logistical disruptions. Correlate regional events with potential earnings impact across your portfolio.",
            "cta": "Explore risk analytics",
            "url": "/products/credit"
          },
          {
            "title": "Financial Health of Key Nodes",
            "details": "Analyze the credit ratings, sentiment, and financial stability of critical suppliers. Model the contagion risk of a key node's failure through the entire chain.",
            "cta": "Assess node stability",
            "url": "/docs"
          }
        ]}
      />

      <Responses
  heading='Map the Web Around Every Company'
  subheading='From the suppliers keeping production running to the customers driving revenue and the rivals shaping the competition - see exactly how AAPL is connected across the market.'
  ticker='AAPL'
  tabs={[
    {
      title: 'Suppliers',
      description: "Key vendors and critical suppliers that AAPL depends on for components, manufacturing, and materials.",
      data:[
              {
                "ticker": "TSM",
                "companyName": "Taiwan Semiconductor Manufacturing Company Limited",
                "marketCap": "2,251,605.86",
                "revenues": "121,423.20",
                "income": "54,036.50",
                "employees": "70,871"
              },
              {
                "ticker": "AVGO",
                "companyName": "Broadcom Inc",
                "marketCap": "1,756,957.85",
                "revenues": "75,465.00",
                "income": "29,317.00",
                "employees": "33,000"
              },
              {
                "ticker": "MU",
                "companyName": "Micron Technology Inc",
                "marketCap": "1,110,187.28",
                "revenues": "42,312.00",
                "income": "11,909.00",
                "employees": "53,000"
              },
              {
                "ticker": "AMD",
                "companyName": "Advanced Micro Devices Inc",
                "marketCap": "854,403.00",
                "revenues": "36,413.00",
                "income": "5,009.00",
                "employees": "31,000"
              },
              {
                "ticker": "INTC",
                "companyName": "Intel Corporation",
                "marketCap": "611,739.05",
                "revenues": "53,763.00",
                "income": "-3,368.00",
                "employees": "85,100"
              },
              {
                "ticker": "AMAT",
                "companyName": "Applied Materials Inc",
                "marketCap": "481,828.96",
                "revenues": "29,024.00",
                "income": "8,508.00",
                "employees": "36,500"
              },
              {
                "ticker": "TXN",
                "companyName": "Texas Instruments Incorporated",
                "marketCap": "267,875.12",
                "revenues": "18,438.00",
                "income": "5,367.00",
                "employees": "33,000"
              },
              {
                "ticker": "MRVL",
                "companyName": "Marvell Technology Inc",
                "marketCap": "219,117.56",
                "revenues": "8,715.90",
                "income": "4,677.50",
                "employees": "7,480"
              },
              {
                "ticker": "APH",
                "companyName": "Amphenol Corporation",
                "marketCap": "212,271.72",
                "revenues": "25,902.90",
                "income": "4,504.40",
                "employees": "170,000"
              },
              {
                "ticker": "WDC",
                "companyName": "Western Digital Corporation",
                "marketCap": "210,670.12",
                "revenues": "9,752.00",
                "income": "6,511.00",
                "employees": "51,000"
              },
              {
                "ticker": "QCOM",
                "companyName": "Qualcomm Inc",
                "marketCap": "188,940.00",
                "revenues": "44,487.00",
                "income": "9,923.00",
                "employees": "52,000"
              },
              {
                "ticker": "STX",
                "companyName": "Seagate Technology Holdings Plc",
                "marketCap": "187,816.64",
                "revenues": "11,010.00",
                "income": "2,378.00",
                "employees": "30,000"
    }]

    },
    {
      title: 'Customers',
      description: "Major customers and key accounts that represent significant revenue concentration for AAPL.",
      data: [
              {
                "ticker": "AVGO",
                "companyName": "Broadcom Inc",
                "marketCap": "1,756,957.85",
                "revenues": "75,465.00",
                "income": "29,317.00",
                "employees": "33,000"
              },
              {
                "ticker": "T",
                "companyName": "At and t Inc",
                "marketCap": "144,615.66",
                "revenues": "126,528.00",
                "income": "22,875.00",
                "employees": "133,030"
              },
              {
                "ticker": "TMUS",
                "companyName": "T mobile Us Inc",
                "marketCap": "195,636.49",
                "revenues": "90,530.00",
                "income": "10,543.00",
                "employees": "75,000"
              },
              {
                "ticker": "VZ",
                "companyName": "Verizon Communications Inc",
                "marketCap": "179,177.60",
                "revenues": "139,146.00",
                "income": "17,771.00",
                "employees": "89,900"
              },
              {
                "ticker": "AMZN",
                "companyName": "Amazon com Inc",
                "marketCap": "2,638,793.58",
                "revenues": "742,776.00",
                "income": "90,798.00",
                "employees": "1,576,000"
              },
              {
                "ticker": "BBY",
                "companyName": "Best Buy Co Inc",
                "marketCap": "16,479.29",
                "revenues": "41,697.00",
                "income": "1,001.00",
                "employees": "82,000"
              },
              {
                "ticker": "BIG",
                "companyName": "Big Lots Inc",
                "marketCap": "14.73",
                "revenues": "4,514.02",
                "income": "-469.46",
                "employees": "30,300"
              },
              {
                "ticker": "PTC",
                "companyName": "Ptc Inc",
                "marketCap": "14,765.78",
                "revenues": "2,996.57",
                "income": "1,246.36",
                "employees": "7,642"
              },
              {
                "ticker": "TGT",
                "companyName": "Target Corporation",
                "marketCap": "59,349.72",
                "revenues": "106,377.00",
                "income": "3,450.00",
                "employees": "415,000"
              },
              {
                "ticker": "COIN",
                "companyName": "Coinbase Global Inc",
                "marketCap": "43,814.97",
                "revenues": "6,560.01",
                "income": "800.60",
                "employees": "4,951"
              },
              {
                "ticker": "EBAY",
                "companyName": "Ebay Inc",
                "marketCap": "52,481.88",
                "revenues": "11,604.00",
                "income": "2,040.00",
                "employees": "12,300"
              },
              {
                "ticker": "GME",
                "companyName": "Gamestop Corp",
                "marketCap": "13,516.29",
                "revenues": "3,732.80",
                "income": "763.20",
                "employees": "15,000"
              },
              {
                "ticker": "LIVE",
                "companyName": "Live Ventures Inc",
                "marketCap": "30.53",
                "revenues": "438.11",
                "income": "3.87",
                "employees": "1,744"
              },
              {
                "ticker": "RIOT",
                "companyName": "Riot Platforms Inc",
                "marketCap": "7,685.97",
                "revenues": "653.27",
                "income": "-867.29",
                "employees": "431"
              },
              {
                "ticker": "WMT",
                "companyName": "Walmart Inc",
                "marketCap": "894,608.16",
                "revenues": "693,127.00",
                "income": "21,008.00",
                "employees": "2,100,000"
              }
      ],
    },
    {
      title: 'Peers',
      description: "Direct competitors and industry peers used for benchmarking financial performance and market positioning.",
      data: [
        { "ticker": "DELL", "name": "Dell Technologies Inc" },
        { "ticker": "IBM", "name": "International Business Machines Corporation" },
        { "ticker": "HPQ", "name": "Hp Inc" },
        { "ticker": "ADBE", "name": "Adobe Inc" },
        { "ticker": "MSFT", "name": "Microsoft Corporation" },
        { "ticker": "ADSK", "name": "Autodesk Inc" },
        { "ticker": "GOOG", "name": "Alphabet Inc" },
        { "ticker": "META", "name": "Meta Platforms Inc" },
        { "ticker": "QCOM", "name": "Qualcomm Inc" },
      ],
    },
  ]}
/>
      <DatasetDetails full={true}
        title="Comprehensive Supply Chain Data Coverage"
        subtitle="Access tier-1 through tier-N supplier and customer networks for any public company through our supply chain analytics API, backed by over 20 million global suppliers and 1,000+ data sources. Track trade flows between 195+ countries and monitor current port congestion at 500+ maritime hubs."
        cta="Get Started Free"
        ctaLink="/login"
        tiles={[
          {
            img: '12.webp',
            title: 'We track 25,000+ companies',
            caption: 'You can map tier-1 through tier-N supplier and customer networks using supply chain data for any public company.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '14.webp',
            title: 'We monitor 1M+ trade routes',
            caption: 'You can analyze trade flows between 195+ countries for supply chain exposure.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '13.webp',
            title: 'We watch 500+ maritime ports',
            caption: 'You can monitor current port congestion and logistics disruptions across global hubs.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '0.webp',
            title: 'We index 20M+ global suppliers',
            caption: 'You can identify hidden portfolio exposures through our extensive supplier network data.',
            cn: 'scaled corner',
            tile: <W4/>
          },
          {
            img: '1.webp',
            title: 'We refresh daily',
            caption: 'You receive updated supply chain intelligence every day for timely risk assessment.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />
      
      <Outcomes
        headline="Risk intelligence that moves markets."
        leftCell={{ stat: '94%', title: 'Early warning accuracy.', desc: '94% of material disruptions captured before earnings call.', showChart: true }}
        rightCells={[
          { stat: '3X',   title: 'Faster supplier risk assessment.', desc: 'Quantify exposure in minutes, not weeks.' },
          { stat: '99.9%',title: 'Data availability SLA.', desc: 'Production-grade reliability for systematic strategies.', boldDesc: true },
        ]}
      /><ProductFAQ slug="supply-chain" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default SupplyChainPage;
